import { api } from '../features/auth/authStore';

interface ActiveTimer {
  id: string;
  taskId?: string;
  taskTitle?: string;
  description: string;
  startTime: Date;
  duration: number;
  billable: boolean;
  status: string;
}

interface StoredTimer extends ActiveTimer {
  lastSyncTime: string;
  userId: string;
}

const TIMER_STORAGE_KEY = 'erpca_active_timer';
const MAX_OFFLINE_TIME = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export class TimerPersistenceService {
  /**
   * Save timer to localStorage
   */
  static saveTimer(timer: ActiveTimer | null, userId: string): void {
    try {
      if (timer) {
        const storedTimer: StoredTimer = {
          ...timer,
          lastSyncTime: new Date().toISOString(),
          userId
        };
        localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(storedTimer));
      } else {
        localStorage.removeItem(TIMER_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving timer to storage:', error);
    }
  }

  /**
   * Load timer from localStorage
   */
  static loadTimer(userId: string): ActiveTimer | null {
    try {
      const stored = localStorage.getItem(TIMER_STORAGE_KEY);
      if (!stored) return null;

      const parsed: StoredTimer = JSON.parse(stored);
      
      // Check if timer belongs to current user
      if (parsed.userId !== userId) {
        localStorage.removeItem(TIMER_STORAGE_KEY);
        return null;
      }

      // Check if timer is too old (prevent stale timers)
      const lastSync = new Date(parsed.lastSyncTime);
      const now = new Date();
      if (now.getTime() - lastSync.getTime() > MAX_OFFLINE_TIME) {
        localStorage.removeItem(TIMER_STORAGE_KEY);
        return null;
      }

      return {
        id: parsed.id,
        taskId: parsed.taskId,
        description: parsed.description,
        startTime: new Date(parsed.startTime),
        duration: parsed.duration,
        billable: parsed.billable,
        status: parsed.status
      };
    } catch (error) {
      console.error('Error loading timer from storage:', error);
      localStorage.removeItem(TIMER_STORAGE_KEY);
      return null;
    }
  }

  /**
   * Sync local timer with server
   */
  static async syncWithServer(userId: string): Promise<{
    serverTimer: ActiveTimer | null;
    localTimer: ActiveTimer | null;
    needsRecovery: boolean;
  }> {
    const localTimer = this.loadTimer(userId);
    let serverTimer: ActiveTimer | null = null;
    let needsRecovery = false;

    try {
      const response = await api.get('/time-entries/active-timer');
      if (response.data.success && response.data.data) {
        serverTimer = {
          ...response.data.data,
          startTime: new Date(response.data.data.startTime)
        };
      }
    } catch (error) {
      console.error('Error fetching server timer:', error);
      // If we can't reach server but have local timer, we might need recovery
      if (localTimer) {
        needsRecovery = true;
      }
    }

    // Determine if recovery is needed
    if (localTimer && !serverTimer) {
      needsRecovery = true;
    } else if (localTimer && serverTimer && localTimer.id !== serverTimer.id) {
      // Different timers - this shouldn't happen but handle gracefully
      needsRecovery = true;
    }

    return { serverTimer, localTimer, needsRecovery };
  }

  /**
   * Recover timer by syncing with server or creating new entry
   */
  static async recoverTimer(localTimer: ActiveTimer, userId: string): Promise<ActiveTimer | null> {
    try {
      // Calculate how long the timer has been running
      const now = new Date();
      const elapsedMinutes = Math.floor((now.getTime() - localTimer.startTime.getTime()) / (1000 * 60));

      // If timer has been running for a reasonable amount of time, try to recover it
      if (elapsedMinutes > 0 && elapsedMinutes < 24 * 60) { // Less than 24 hours
        // Try to start a new timer with the same details
        const response = await api.post('/time-entries/start-timer', {
          taskId: localTimer.taskId,
          description: localTimer.description
        });

        if (response.data.success) {
          const newTimer = {
            ...response.data.data,
            startTime: new Date(response.data.data.startTime)
          };
          
          // Save the recovered timer
          this.saveTimer(newTimer, userId);
          return newTimer;
        }
      }
    } catch (error) {
      console.error('Error recovering timer:', error);
    }

    // If recovery fails, clear the local timer
    this.saveTimer(null, userId);
    return null;
  }

  /**
   * Handle timer conflicts between local and server
   */
  static async resolveConflict(
    localTimer: ActiveTimer,
    serverTimer: ActiveTimer,
    userId: string
  ): Promise<ActiveTimer> {
    // For now, prefer server timer as source of truth
    // In the future, we could show a dialog to let user choose
    
    console.warn('Timer conflict detected, using server timer', {
      local: localTimer,
      server: serverTimer
    });

    this.saveTimer(serverTimer, userId);
    return serverTimer;
  }

  /**
   * Clear all stored timer data
   */
  static clearStorage(): void {
    try {
      localStorage.removeItem(TIMER_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing timer storage:', error);
    }
  }

  /**
   * Check if there's a stored timer for the user
   */
  static hasStoredTimer(userId: string): boolean {
    return this.loadTimer(userId) !== null;
  }

  /**
   * Get elapsed time for stored timer
   */
  static getElapsedTime(timer: ActiveTimer): number {
    const now = new Date();
    return Math.floor((now.getTime() - timer.startTime.getTime()) / 1000);
  }

  /**
   * Validate timer data
   */
  static isValidTimer(timer: any): timer is ActiveTimer {
    return (
      timer &&
      typeof timer.id === 'string' &&
      typeof timer.description === 'string' &&
      timer.startTime instanceof Date &&
      typeof timer.duration === 'number' &&
      typeof timer.billable === 'boolean' &&
      typeof timer.status === 'string'
    );
  }
}

// Export singleton instance
export const timerPersistence = TimerPersistenceService;
